import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { USER_ROLES } from 'utils/constants';
import { rawId } from 'utils/rawId';
import { goToNewSpace, goToSpaces } from 'utils/routerHelpers';
import { canAccess } from 'utils/userHelpers';

import React, { useState } from 'react';

import { Button } from '@thinkific/chalk/Button';
import { Plus } from '@thinkific/chalk/react-icons';
import { ExpandableMenuSection } from '@thinkific/chalk/recipes/ExpandableMenuSection';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { useSpaces } from 'components/new/providers/SpacesProvider/SpacesProvider';
import NavList from 'components/new/ui/MenuList/MenuList';

import SpacesItem from './SpacesItem';

export const SpacesList = ({ community, trackEvent, currentUser }) => {
  const { t } = useTranslation();
  const [sectionsExpanded, setSectionsExpanded] = useState(true);
  const canAddSpace = canAccess(
    [USER_ROLES.SITE_OWNER_SITE_ADMIN, USER_ROLES.SITE_ADMIN, USER_ROLES.PARTNER],
    currentUser
  );
  const { spaceList, onSpaceDragEnd } = useSpaces();
  const router = useRouter();

  const handleCreateSpaceClick = () => {
    trackEvent(TrackingEvent.GO_TO_NEW_SPACE, {
      [TrackingProperty.COMPONENT]: 'SpacesList',
    });
    goToNewSpace(router, community.id);
  };

  const handleSpaceClick = (filter: string, name: string) => {
    trackEvent(TrackingEvent.SPACE_CLICKED, {
      [TrackingProperty.COMMUNITY_ID]: rawId(community.id),
      [TrackingProperty.SPACE_ID]: rawId(filter),
      [TrackingProperty.SPACE_NAME]: name,
    });

    goToSpaces(router, filter, community.id);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  return (
    <ExpandableMenuSection.Toggle
      dataQa="sidebar-spaces"
      open={sectionsExpanded}
      title={t('sidebar-spaces', 'Spaces')}
      titleItem={
        canAddSpace ? (
          <Button
            data-user-pilot-id="create-space-button"
            emphasis="low"
            icon={Plus}
            size="xs"
            hiddenLabel
            onClick={() => handleCreateSpaceClick()}
          >
            {t('sidebar-spaces-create', 'Create Space')}
          </Button>
        ) : null
      }
      isFilledCaret
      onToggle={() => setSectionsExpanded(!sectionsExpanded)}
    >
      <DndContext collisionDetection={closestCenter} sensors={sensors} onDragEnd={onSpaceDragEnd}>
        <SortableContext items={spaceList} strategy={verticalListSortingStrategy}>
          <NavList>
            {spaceList.map((space) => (
              <SpacesItem goToFunction={handleSpaceClick} key={space.id} space={space} />
            ))}
          </NavList>
        </SortableContext>
      </DndContext>
    </ExpandableMenuSection.Toggle>
  );
};

export default SpacesList;
